import type { L10n } from '../types'
import { engagementConfig, hiveCollectibles, hiveStages, type WeekdayId } from './engagement'
import { beeArchetypes } from './recapArchetypes'
import type { RecapModule } from '../types/engagement'

/* ------------------------------------------------------------------ *
 * Admin view of the engagement system.
 * FIXED PRODUCT RULES are read-only. CONFIGURABLE PARAMETERS mirror
 * `engagementConfig` (single source) and are editable only with the
 * `engagement.manageConfig` capability, with confirmation + audit.
 * REQUIRES BACKEND: serving, versioning and authorizing these values.
 * ------------------------------------------------------------------ */

export type EngagementAdminSection = 'protection' | 'hive' | 'chest' | 'league' | 'recap'

const recapModuleLabels: Record<RecapModule, L10n> = {
  beeType: { 'pt-BR': 'Seu tipo de abelha', 'en-US': 'Your Bee type' },
  hive: { 'pt-BR': 'Evolução da Colmeia', 'en-US': 'Hive evolution' },
  consistency: { 'pt-BR': 'Constância', 'en-US': 'Consistency' },
  habit: { 'pt-BR': 'Hábito mais constante', 'en-US': 'Most consistent habit' },
  activity: { 'pt-BR': 'Atividade principal', 'en-US': 'Main activity' },
  community: { 'pt-BR': 'Participação na comunidade', 'en-US': 'Community participation' },
  social: { 'pt-BR': 'Quem esteve com você', 'en-US': 'Who was with you' },
  percentile: { 'pt-BR': 'Consistência na comunidade', 'en-US': 'Community consistency' },
  milestones: { 'pt-BR': 'Grandes marcos (anual)', 'en-US': 'Major milestones (annual)' },
}

export interface ConfigParamOption {
  id: string
  label: L10n
}

export interface ConfigParam {
  id: string
  label: L10n
  hint?: L10n
  /** Current value as displayed/edited. */
  value: string
  kind: 'number' | 'boolean' | 'select' | 'text'
  unit?: L10n
  options?: ConfigParamOption[]
}

/** Rules that apply to every engagement feature. Never configurable. */
export const coreFixedRules: L10n[] = [
  { 'pt-BR': 'Base do Daily Check = 100 Bee', 'en-US': 'Daily Check base = 100 Bee' },
  {
    'pt-BR': 'Post completo liberado com 50 Bee de base do Daily Check no dia',
    'en-US': 'Full Post unlocks at 50 Daily Check base Bee in the day',
  },
  {
    'pt-BR': 'Bee de bônus (Baú da Colmeia, Foto do Dia e outros) não conta para liberar o Post',
    'en-US': 'Bonus Bee (Hive Chest, Photo of the Day and others) never counts toward the Post unlock',
  },
]

const days = (n: number) => String(n)
const unitDays: L10n = { 'pt-BR': 'dias', 'en-US': 'days' }
const unitBee: L10n = { 'pt-BR': 'Bee', 'en-US': 'Bee' }
const yesNo: ConfigParamOption[] = [
  { id: 'true', label: { 'pt-BR': 'Ligada', 'en-US': 'On' } },
  { id: 'false', label: { 'pt-BR': 'Desligada', 'en-US': 'Off' } },
]

const weekdayOptions: { id: WeekdayId; label: L10n }[] = [
  { id: 'mon', label: { 'pt-BR': 'Segunda', 'en-US': 'Monday' } },
  { id: 'tue', label: { 'pt-BR': 'Terça', 'en-US': 'Tuesday' } },
  { id: 'wed', label: { 'pt-BR': 'Quarta', 'en-US': 'Wednesday' } },
  { id: 'thu', label: { 'pt-BR': 'Quinta', 'en-US': 'Thursday' } },
  { id: 'fri', label: { 'pt-BR': 'Sexta', 'en-US': 'Friday' } },
  { id: 'sat', label: { 'pt-BR': 'Sábado', 'en-US': 'Saturday' } },
  { id: 'sun', label: { 'pt-BR': 'Domingo', 'en-US': 'Sunday' } },
]

const triggerLabels: Record<string, string> = {
  streakMilestone: 'marco de sequência',
  perfectDay: 'dia perfeito',
  challengeDone: 'desafio concluído',
  returning: 'retorno após pausa',
}

const { streakProtection, hive, hiveChest, league, recap } = engagementConfig

export const engagementAdminSections: Record<
  EngagementAdminSection,
  { title: L10n; fixedRules: L10n[]; params: ConfigParam[] }
> = {
  protection: {
    title: { 'pt-BR': 'Proteção de Sequência', 'en-US': 'Streak Protection' },
    fixedRules: [
      { 'pt-BR': 'Uma proteção cobre um único dia perdido', 'en-US': 'One protection covers a single missed day' },
      {
        'pt-BR': 'O dia protegido nunca conta como concluído: não gera Bee nem altera o Life Score',
        'en-US': 'A protected day never counts as completed: no Bee and no Life Score change',
      },
    ],
    params: [
      {
        id: 'streakProtection.defaultEnabled',
        label: { 'pt-BR': 'Estado padrão para novos usuários', 'en-US': 'Default state for new users' },
        value: String(streakProtection.defaultEnabled),
        kind: 'boolean',
        options: yesNo,
      },
      {
        id: 'streakProtection.minStreakDays',
        label: { 'pt-BR': 'Sequência mínima para elegibilidade', 'en-US': 'Minimum streak for eligibility' },
        value: days(streakProtection.minStreakDays),
        kind: 'number',
        unit: unitDays,
      },
      {
        id: 'streakProtection.rechargeDays',
        label: { 'pt-BR': 'Período de recarga', 'en-US': 'Recharge period' },
        hint: { 'pt-BR': 'Dias concluídos seguidos', 'en-US': 'Consecutive completed days' },
        value: days(streakProtection.rechargeDays),
        kind: 'number',
        unit: unitDays,
      },
    ],
  },
  hive: {
    title: { 'pt-BR': 'Minha Colmeia', 'en-US': 'My Hive' },
    fixedRules: [
      { 'pt-BR': 'Cinco estágios visuais', 'en-US': 'Five visual stages' },
      { 'pt-BR': 'Não altera o Life Score nem os níveis', 'en-US': 'Never changes Life Score or levels' },
    ],
    params: [
      {
        id: 'hive.beePerCell',
        label: { 'pt-BR': 'Bee por célula', 'en-US': 'Bee per cell' },
        value: String(hive.beePerCell),
        kind: 'number',
        unit: unitBee,
      },
      ...hiveStages.map<ConfigParam>((stage, index) => ({
        id: `hive.stageThresholds.${stage.id}`,
        label: {
          'pt-BR': `Estágio ${index + 1} · ${stage.name['pt-BR']}`,
          'en-US': `Stage ${index + 1} · ${stage.name['en-US']}`,
        },
        hint: { 'pt-BR': 'Bee acumulado para iniciar', 'en-US': 'Cumulative Bee to start' },
        value: String(stage.minBee),
        kind: 'number',
        unit: unitBee,
      })),
      ...hiveCollectibles.map<ConfigParam>((item) => ({
        id: `hive.collectibles.${item.id}`,
        label: {
          'pt-BR': `Colecionável · ${item.name['pt-BR']}`,
          'en-US': `Collectible · ${item.name['en-US']}`,
        },
        hint: { 'pt-BR': 'Marco de desbloqueio', 'en-US': 'Unlock milestone' },
        value: item.rule['pt-BR'],
        kind: 'text',
      })),
    ],
  },
  chest: {
    title: { 'pt-BR': 'Baú da Colmeia', 'en-US': 'Hive Chest' },
    fixedRules: [
      { 'pt-BR': 'Bônus de Bee fora da base de 100 Bee', 'en-US': 'Bee bonus outside the 100 Bee base' },
      { 'pt-BR': 'Nunca envolve compra nem linguagem de aposta', 'en-US': 'Never involves purchases or gambling language' },
      { 'pt-BR': 'Não conta para liberar o Post', 'en-US': 'Never counts toward the Post unlock' },
    ],
    params: [
      {
        id: 'hiveChest.eligibleTriggers',
        label: { 'pt-BR': 'Marcos elegíveis', 'en-US': 'Eligible milestones' },
        value: hiveChest.eligibleTriggers.map((id) => triggerLabels[id] ?? id).join(', '),
        kind: 'text',
      },
      {
        id: 'hiveChest.streakMilestoneDays',
        label: { 'pt-BR': 'Marco de sequência', 'en-US': 'Streak milestone' },
        value: days(hiveChest.streakMilestoneDays),
        kind: 'number',
        unit: unitDays,
      },
      {
        id: 'hiveChest.maxPerWindow',
        label: { 'pt-BR': 'Limite de baús por janela', 'en-US': 'Max chests per window' },
        value: String(hiveChest.maxPerWindow),
        kind: 'number',
      },
      {
        id: 'hiveChest.windowDays',
        label: { 'pt-BR': 'Janela de frequência', 'en-US': 'Frequency window' },
        value: days(hiveChest.windowDays),
        kind: 'number',
        unit: unitDays,
      },
      {
        id: 'hiveChest.minBonusBee',
        label: { 'pt-BR': 'Bônus mínimo', 'en-US': 'Minimum bonus' },
        value: String(hiveChest.minBonusBee),
        kind: 'number',
        unit: unitBee,
      },
      {
        id: 'hiveChest.maxBonusBee',
        label: { 'pt-BR': 'Bônus máximo', 'en-US': 'Maximum bonus' },
        value: String(hiveChest.maxBonusBee),
        kind: 'number',
        unit: unitBee,
      },
    ],
  },
  league: {
    title: { 'pt-BR': 'Liga semanal', 'en-US': 'Weekly league' },
    fixedRules: [
      { 'pt-BR': 'Ciclo semanal de 7 dias', 'en-US': 'Weekly seven-day cycle' },
      { 'pt-BR': 'Opcional e desligada até o usuário entrar', 'en-US': 'Optional and off until the user joins' },
      {
        'pt-BR': 'Sem ranking global; nunca pontua Life Score, corpo ou dados de saúde',
        'en-US': 'No global ranking; never scores Life Score, body or health data',
      },
    ],
    params: [
      {
        id: 'league.groupSize',
        label: { 'pt-BR': 'Tamanho do grupo', 'en-US': 'Group size' },
        value: String(league.groupSize),
        kind: 'number',
        unit: { 'pt-BR': 'pessoas', 'en-US': 'people' },
      },
      {
        id: 'league.scoringRule',
        label: { 'pt-BR': 'Regra de pontuação', 'en-US': 'Scoring rule' },
        value: league.scoringRule,
        kind: 'select',
        options: [
          { id: 'checkDays', label: { 'pt-BR': 'Dias com Daily Check', 'en-US': 'Daily Check days' } },
          {
            id: 'checkDaysAndChallenges',
            label: { 'pt-BR': 'Dias com Daily Check + desafios', 'en-US': 'Daily Check days + challenges' },
          },
        ],
      },
      {
        id: 'league.resetDay',
        label: { 'pt-BR': 'Dia de início / reinício do ciclo', 'en-US': 'Cycle start / reset day' },
        value: league.resetDay,
        kind: 'select',
        options: weekdayOptions,
      },
    ],
  },
  recap: {
    title: { 'pt-BR': 'Retrospectiva', 'en-US': 'Recap' },
    fixedRules: [
      {
        'pt-BR': 'O tipo de abelha usa só a constância dos hábitos do Daily Check — nunca biomarcadores, peso, composição corporal, humor ou métricas privadas de saúde',
        'en-US': 'The Bee type uses only Daily Check habit consistency — never biomarkers, weight, body composition, mood or private health metrics',
      },
      {
        'pt-BR': 'Empate no topo resulta em Abelha do Equilíbrio, nunca em um vencedor arbitrário',
        'en-US': 'A tie at the top results in the Balance Bee, never an arbitrary winner',
      },
      {
        'pt-BR': 'Percentil da comunidade: só a posição do próprio usuário, pela constância do Daily Check; sem nomes, pontuações nem ranking numerado',
        'en-US': 'Community percentile: only the user’s own context, by Daily Check consistency; no names, scores or numbered leaderboard',
      },
      {
        'pt-BR': 'Quem esteve com você: só curtidas e comentários em conteúdo publicado, respeitando bloqueios e visibilidade',
        'en-US': 'Who was with you: only likes and comments on published content, respecting blocks and visibility',
      },
      {
        'pt-BR': 'Bônus de compartilhamento: opcional, no máximo um por retrospectiva, Bee de bônus que não conta para liberar o Post; abrir a retrospectiva não gera Bee',
        'en-US': 'Share bonus: optional, at most one per recap, bonus Bee that never counts toward the Post unlock; opening a recap earns nothing',
      },
      {
        'pt-BR': 'Dados de saúde nunca entram no card por padrão; compartilhar é sempre explícito',
        'en-US': 'Health data never goes on the card by default; sharing is always explicit',
      },
    ],
    params: [
      {
        id: 'recap.shareRewardBee',
        label: { 'pt-BR': 'Bônus de compartilhamento', 'en-US': 'Share bonus' },
        hint: { 'pt-BR': 'Valor de exemplo, por retrospectiva', 'en-US': 'Sample value, per recap' },
        value: String(recap.shareRewardBee),
        kind: 'number',
        unit: unitBee,
      },
      {
        id: 'recap.minCommunityPopulation',
        label: { 'pt-BR': 'População mínima para percentil', 'en-US': 'Minimum population for percentile' },
        hint: { 'pt-BR': 'Membros elegíveis no período', 'en-US': 'Eligible members in the period' },
        value: String(recap.minCommunityPopulation),
        kind: 'number',
        unit: { 'pt-BR': 'membros', 'en-US': 'members' },
      },
      {
        id: 'recap.minSocialInteractions',
        label: { 'pt-BR': 'Mínimo de interações para “Quem esteve com você”', 'en-US': 'Minimum interactions for “Who was with you”' },
        value: String(recap.minSocialInteractions),
        kind: 'number',
      },
      ...(Object.keys(recap.modules) as RecapModule[]).map<ConfigParam>((id) => ({
        id: `recap.modules.${id}`,
        label: {
          'pt-BR': `Módulo · ${recapModuleLabels[id]['pt-BR']}`,
          'en-US': `Module · ${recapModuleLabels[id]['en-US']}`,
        },
        value: String(recap.modules[id]),
        kind: 'boolean',
        options: yesNo,
      })),
      ...(Object.keys(beeArchetypes) as (keyof typeof beeArchetypes)[]).map<ConfigParam>((id) => ({
        id: `recap.archetypes.${id}`,
        label: {
          'pt-BR': `Tipo de abelha · ${beeArchetypes[id].name['pt-BR']}`,
          'en-US': `Bee type · ${beeArchetypes[id].name['en-US']}`,
        },
        hint: { 'pt-BR': 'Nome e descrição (pt-BR / en-US)', 'en-US': 'Name and description (pt-BR / en-US)' },
        value: beeArchetypes[id].name['pt-BR'],
        kind: 'text',
      })),
    ],
  },
}
